import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSendInvitation } from '@/hooks/useInvitations';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'atendente' | 'prestador'>('atendente');
  const send = useSendInvitation();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await send.mutateAsync({ email: email.toLowerCase().trim(), role });
      setEmail('');
      setRole('atendente');
      onOpenChange(false);
    } catch {
      // toast handled by hook's onError
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('invite.dialog.title')}</DialogTitle>
          <DialogDescription>{t('invite.dialog.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">{t('invite.dialog.emailLabel')}</Label>
            <Input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('invite.dialog.emailPlaceholder')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-role">{t('invite.dialog.roleLabel')}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as typeof role)}>
              <SelectTrigger id="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{t('invite.dialog.roles.admin')}</SelectItem>
                <SelectItem value="atendente">{t('invite.dialog.roles.atendente')}</SelectItem>
                <SelectItem value="prestador">{t('invite.dialog.roles.prestador')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={send.isPending}>
              {send.isPending ? t('invite.dialog.sending') : t('invite.dialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
